import React, { Suspense, useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AccessibleFocusManagement,
  Loader,
  ScrollToTop,
  Spinner,
  Sprite as UIKitSprite,
} from 'bsd-react-ui-kit';

// components
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import Sprite from 'components/Sprite/Sprite';

// actions
import { actions as appActions } from 'modules/application';

// selectors
import { getApplicationLoaded } from 'modules/application/selectors';
import { getSpinner } from 'modules/common/selectors';

// constants
import routes from 'routes';
import config from 'config';
const { globalNavConfig } = config;

export default function App() {
  const dispatch = useDispatch();
  const loaded = useSelector(getApplicationLoaded);
  const spinner = useSelector(getSpinner);
  const { pathname } = useLocation();

  useEffect(() => {
    if (document.getElementById('bsd-global-nav')) return;

    const scriptTag = document.createElement('script');

    scriptTag.setAttribute('src', globalNavConfig.scriptUrl);
    scriptTag.setAttribute('id', 'bsd-global-nav');
    scriptTag.setAttribute('data-environment', globalNavConfig.environment);
    scriptTag.setAttribute('data-application-type', 'RESOURCE_URL');
    scriptTag.setAttribute('data-application-root-path', '/invite/enter/');
    scriptTag.setAttribute('data-application-name', 'PrimaryInvite');
    scriptTag.setAttribute('data-flow-header', '{ "hideCloseButton": "true" }');
    scriptTag.setAttribute(
      'data-components',
      '[{ "type": "FLOW_HEADER", "elementId": "bsd-global-nav-header"}, { "type": "FLOW_FOOTER", "elementId": "bsd-global-nav-footer"}]'
    );
    document.body.appendChild(scriptTag);
  }, []);

  useEffect(() => {
    dispatch(appActions.appInit());
  }, [dispatch]);

  // update session on location changes
  useEffect(() => {
    dispatch(appActions.updateSessionId());
  }, [dispatch, pathname]);

  const renderLayout = (route, props) => {
    const { component, layout, layoutProps, pageTitle, path } = route;
    const Layout = layout;

    return (
      <Layout
        component={component}
        layoutProps={layoutProps}
        pageTitle={pageTitle}
        path={path}
        {...props}
      />
    );
  };

  return (
    <Loader
      loaded={loaded.session}
      render={() => (
        <div
          className="bsd-main-content min-content-height"
          id="main"
          role="main"
        >
          <AccessibleFocusManagement />
          <ScrollToTop />
          <UIKitSprite>
            <Sprite />
          </UIKitSprite>
          <ErrorBoundary>
            <Suspense fallback={<Spinner show />}>
              <Spinner modifier="global" {...spinner} />
              <Switch>
                {routes.map((route) => {
                  const { exact, path } = route;

                  return (
                    <Route
                      exact={exact}
                      key={path}
                      path={path}
                      render={(props) => renderLayout(route, props)}
                    />
                  );
                })}
              </Switch>
            </Suspense>
          </ErrorBoundary>
        </div>
      )}
    />
  );
}
